// modals/bank/EditBankAccount.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Save,
    X,
    User,
    //Building, 
    Hash,
    CreditCard,
    MapPin,
    Phone,
    Mail,
    DollarSign,
    FileText,
    Home
} from 'react-feather';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { bankActions } from '../../action/bankActions';

const EditBankAccount = ({ accountId, onSuccess, onClose, isOpen = false }) => {
    const [formData, setFormData] = useState({
        id: '',
        account_name: '',
        bank_name: '',
        account_number: '',
        account_type: 'savings',
        ifsc_code: '',
        branch_name: '',
        opening_balance: 0,
        current_balance: 0,
        currency: 'INR',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        address: '',
        description: '',
        is_default: false,
        status: 'active'
    });

    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    // Account type options
    const accountTypeOptions = [
        { value: 'savings', label: 'Savings Account' },
        { value: 'current', label: 'Current Account' },
        { value: 'cash', label: 'Cash Account' },
        { value: 'credit_card', label: 'Credit Card' },
        { value: 'loan', label: 'Loan Account' }
    ];

    // Currency options
    const currencyOptions = [
        { value: 'INR', label: '₹ Indian Rupee' },
        { value: 'USD', label: '$ US Dollar' },
        { value: 'EUR', label: '€ Euro' },
        { value: 'GBP', label: '£ British Pound' }
    ];

    // Status options
    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
    ];

    // Clean up modal backdrops
    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    // Show Bootstrap modal
    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('edit-bank-account');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    // Hide Bootstrap modal
    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('edit-bank-account');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    // Load account data
    const loadAccountData = async () => {
        try {
            setLoading(true);
            const result = await bankActions.getAccount(accountId);

            if (result.success) {
                const account = result.data.account || result.data;

                setFormData({
                    id: account.id,
                    account_name: account.account_name || '',
                    bank_name: account.bank_name || '',
                    account_number: account.account_number || '',
                    account_type: account.account_type || 'savings',
                    ifsc_code: account.ifsc_code || '',
                    branch_name: account.branch_name || '',
                    opening_balance: parseFloat(account.opening_balance || 0),
                    current_balance: parseFloat(account.current_balance || 0),
                    currency: account.currency || 'INR',
                    contact_person: account.contact_person || '',
                    contact_phone: account.contact_phone || '',
                    contact_email: account.contact_email || '',
                    address: account.address || '',
                    description: account.description || '',
                    is_default: account.is_default === 1,
                    status: account.status || 'active'
                });

                setOriginalData(account);

                setTimeout(() => showBootstrapModal(), 100);
            } else {
                Swal.fire('Error', result.message || 'Failed to load account data', 'error');
            }
        } catch (error) {
            console.error('Error loading account:', error);
            Swal.fire('Error', 'Failed to load account data', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accountId) {
            loadAccountData();
        }

        return () => {
            cleanupModalBackdrops();
        };
    }, [accountId]);

    useEffect(() => {
        if (isOpen && !loading && formData.id) {
            setTimeout(() => showBootstrapModal(), 100);
        }
    }, [isOpen, loading, formData.id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSelectChange = (name, option) => {
        setFormData(prev => ({
            ...prev,
            [name]: option?.value || ''
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.account_name.trim()) {
            newErrors.account_name = 'Account holder name is required';
        }

        if (!formData.bank_name.trim()) {
            newErrors.bank_name = 'Bank name is required';
        }

        if (!formData.account_number.trim()) {
            newErrors.account_number = 'Account number is required';
        }

        if (formData.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code)) {
            newErrors.ifsc_code = 'Enter a valid IFSC code';
        }

        const openingBalance = parseFloat(formData.opening_balance || 0);
        const currentBalance = parseFloat(formData.current_balance || 0);

        if (Number.isNaN(openingBalance) || openingBalance < 0) {
            newErrors.opening_balance = 'Opening balance cannot be negative';
        }

        if (Number.isNaN(currentBalance) || currentBalance < 0) {
            newErrors.current_balance = 'Current balance cannot be negative';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasChanges = () => {
        if (!originalData) return false;

        return (
            formData.account_name !== originalData.account_name ||
            formData.bank_name !== originalData.bank_name ||
            formData.account_number !== originalData.account_number ||
            formData.account_type !== originalData.account_type ||
            formData.ifsc_code !== originalData.ifsc_code ||
            formData.branch_name !== originalData.branch_name ||
            parseFloat(formData.opening_balance || 0) !== parseFloat(originalData.opening_balance || 0) ||
            parseFloat(formData.current_balance || 0) !== parseFloat(originalData.current_balance || 0) ||
            formData.currency !== originalData.currency ||
            formData.contact_person !== originalData.contact_person ||
            formData.contact_phone !== originalData.contact_phone ||
            formData.contact_email !== originalData.contact_email ||
            formData.address !== originalData.address ||
            formData.description !== originalData.description ||
            !!formData.is_default !== (parseInt(originalData.is_default, 10) === 1) ||
            formData.status !== originalData.status
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        if (!hasChanges()) {
            Swal.fire('Info', 'No changes to save', 'info');
            return;
        }

        try {
            setSaving(true);

            const result = await bankActions.updateAccount(accountId, formData);

            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Bank account updated successfully',
                    timer: 2000
                });

                hideBootstrapModal();
                if (onSuccess) onSuccess();
            } else {
                Swal.fire('Error', result.message || 'Failed to update account', 'error');
            }
        } catch (error) {
            console.error('Error updating account:', error);
            Swal.fire('Error', error?.message || 'Failed to update account', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges()) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    hideBootstrapModal();
                    if (onClose) onClose();
                }
            });
        } else {
            hideBootstrapModal();
            if (onClose) onClose();
        }
    };

    if (loading) {
        return (
            <div className="modal fade" id="edit-bank-account" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Bank Account</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading account data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal fade" id="edit-bank-account" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Bank Account - {formData.account_name}</h5>
                        <button type="button" className="btn-close" onClick={handleCancel}></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            {/* Account Information */}
                            <h6 className="fw-bold mb-3">Account Information</h6>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <User size={14} className="me-1" />
                                        Account Holder Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.account_name ? 'is-invalid' : ''}`}
                                        name="account_name"
                                        value={formData.account_name}
                                        onChange={handleChange}
                                        placeholder="Enter account holder name"
                                    />
                                    {errors.account_name && (
                                        <div className="invalid-feedback">{errors.account_name}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <Home size={14} className="me-1" />
                                        Bank Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.bank_name ? 'is-invalid' : ''}`}
                                        name="bank_name"
                                        value={formData.bank_name}
                                        onChange={handleChange}
                                        placeholder="Enter bank name"
                                    />
                                    {errors.bank_name && (
                                        <div className="invalid-feedback">{errors.bank_name}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <Hash size={14} className="me-1" />
                                        Account Number <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.account_number ? 'is-invalid' : ''}`}
                                        name="account_number"
                                        value={formData.account_number}
                                        onChange={handleChange}
                                        placeholder="Enter account number"
                                        readOnly // Usually account number shouldn't be changed
                                    />
                                    {errors.account_number && (
                                        <div className="invalid-feedback">{errors.account_number}</div>
                                    )}
                                    <small className="text-muted">Account number cannot be changed</small>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <CreditCard size={14} className="me-1" />
                                        Account Type
                                    </label>
                                    <Select
                                        options={accountTypeOptions}
                                        value={accountTypeOptions.find(opt => opt.value === formData.account_type)}
                                        onChange={(opt) => handleSelectChange('account_type', opt)}
                                        placeholder="Select type"
                                        isClearable={false}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">IFSC Code</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.ifsc_code ? 'is-invalid' : ''}`}
                                        name="ifsc_code"
                                        value={formData.ifsc_code}
                                        onChange={handleChange}
                                        placeholder="e.g., SBIN0123456"
                                        maxLength="11"
                                    />
                                    {errors.ifsc_code && (
                                        <div className="invalid-feedback">{errors.ifsc_code}</div>
                                    )}
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Branch Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="branch_name"
                                        value={formData.branch_name}
                                        onChange={handleChange}
                                        placeholder="Enter branch name"
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Currency</label>
                                    <Select
                                        options={currencyOptions}
                                        value={currencyOptions.find(opt => opt.value === formData.currency)}
                                        onChange={(opt) => handleSelectChange('currency', opt)}
                                        placeholder="Select currency"
                                        isClearable={false}
                                    />
                                </div>
                            </div>

                            {/* Balance Information */}
                            <h6 className="fw-bold mb-3 mt-4">Balance Information</h6>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <DollarSign size={14} className="me-1" />
                                        Opening Balance
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.opening_balance ? 'is-invalid' : ''}`}
                                            name="opening_balance"
                                            value={formData.opening_balance}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.01"
                                            disabled
                                        />
                                    </div>
                                    {errors.opening_balance && (
                                        <div className="text-danger small mt-1">{errors.opening_balance}</div>
                                    )}
                                    <small className="text-muted">Admin can update opening balance</small>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <DollarSign size={14} className="me-1" />
                                        Current Balance
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.current_balance ? 'is-invalid' : ''}`}
                                            name="current_balance"
                                            value={formData.current_balance}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.01"
                                            disabled
                                        />
                                    </div>
                                    {errors.current_balance && (
                                        <div className="text-danger small mt-1">{errors.current_balance}</div>
                                    )}
                                    <small className="text-muted">Admin can update current balance</small>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Status</label>
                                    <Select
                                        options={statusOptions}
                                        value={statusOptions.find(opt => opt.value === formData.status)}
                                        onChange={(opt) => handleSelectChange('status', opt)}
                                        placeholder="Select status"
                                    />
                                </div>

                                <div className="col-12">
                                    <label className="form-label fw-bold">
                                        <FileText size={14} className="me-1" />
                                        Description
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="2"
                                        placeholder="Additional notes about this account"
                                    />
                                </div>
                            </div>

                            {/* Contact Information */}
                            <h6 className="fw-bold mb-3 mt-4">Contact Information</h6>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Contact Person</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="contact_person"
                                        value={formData.contact_person}
                                        onChange={handleChange}
                                        placeholder="Contact person name"
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <Phone size={14} className="me-1" />
                                        Phone
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.contact_phone ? 'is-invalid' : ''}`}
                                        name="contact_phone"
                                        value={formData.contact_phone}
                                        onChange={handleChange}
                                        placeholder="Phone number"
                                    />
                                    {errors.contact_phone && (
                                        <div className="invalid-feedback">{errors.contact_phone}</div>
                                    )}
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <Mail size={14} className="me-1" />
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className={`form-control ${errors.contact_email ? 'is-invalid' : ''}`}
                                        name="contact_email"
                                        value={formData.contact_email}
                                        onChange={handleChange}
                                        placeholder="Email address"
                                    />
                                    {errors.contact_email && (
                                        <div className="invalid-feedback">{errors.contact_email}</div>
                                    )}
                                </div>

                                <div className="col-12">
                                    <label className="form-label fw-bold">
                                        <MapPin size={14} className="me-1" />
                                        Address
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        rows="2"
                                        placeholder="Enter address"
                                    />
                                </div>
                            </div>

                            {/* Additional Options */}
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="is_default"
                                            name="is_default"
                                            checked={formData.is_default}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label" htmlFor="is_default">
                                            Set as default account for transactions
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="text-end mt-4">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={handleCancel}
                                >
                                    <X size={14} className="me-1" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving || !hasChanges()}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={14} className="me-1" />
                                            Update Account
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

EditBankAccount.propTypes = {
    accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool
};

export default EditBankAccount;